import React from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell } from "recharts";
import { formatMoney } from "../lib/api";

const pct = (v) => `${v.toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
const rot = (v) => (v == null ? "—" : `${v.toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}×`);

export const ClasesStockVenta = ({ data, colors, onClick }) => {
  const totS = data.reduce((t, d) => t + (d.stock || 0), 0);
  const totV = data.reduce((t, d) => t + Math.max(0, d.sales || 0), 0);
  const rows = data.map((d) => ({
    ...d,
    ventas: Math.max(0, d.sales || 0),
    pStock: totS ? (100 * (d.stock || 0)) / totS : 0,
    pVenta: totV ? (100 * Math.max(0, d.sales || 0)) / totV : 0,
    rotacion: d.stock ? Math.max(0, d.sales || 0) / d.stock : null,
  }));
  const rotGlobal = totS ? totV / totS : null;
  return (
    <div className="card p-6 mb-8" data-testid="clases-stock-venta">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="font-display text-lg font-bold">Stock vs venta por clase</h3>
          <p className="text-xs text-slate-500">€ en stock frente a € vendidos en el año y rotación en euros (venta ÷ stock) de cada clase.</p>
        </div>
        <div className="text-xs text-slate-500" data-testid="rotacion-global-eur">Rotación global en €: <b className="text-slate-900">{rot(rotGlobal)}</b></div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={rows} barGap={3}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
          <YAxis yAxisId="eur" tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => `${Math.round(v / 1000)}k €`} />
          <YAxis yAxisId="rot" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}×`} />
          <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 6, fontSize: 12 }}
                   formatter={(v, name) => [name === "Rotación (€)" ? rot(v) : formatMoney(v), name]} />
          <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="eur" dataKey="stock" name="Stock €" fill="#94a3b8" radius={[4, 4, 0, 0]} onClick={(d) => onClick?.(d.code)} />
          <Bar yAxisId="eur" dataKey="ventas" name="Venta €" fill="#10b981" radius={[4, 4, 0, 0]} onClick={(d) => onClick?.(d.code)}>
            {rows.map((d) => <Cell key={d.code} fill={colors[d.code]} />)}
          </Bar>
          <Line yAxisId="rot" dataKey="rotacion" name="Rotación (€)" stroke="#0f172a" strokeWidth={2} dot={{ r: 4 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="overflow-x-auto mt-4">
        <table className="dense" data-testid="clases-stock-venta-tabla">
          <thead>
            <tr><th>Clase</th><th className="num">Stock €</th><th className="num">% stock</th><th className="num">Venta €</th><th className="num">% venta</th><th className="num">Rotación €</th></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.code} className={onClick ? "cursor-pointer" : ""} onClick={() => onClick?.(d.code)} data-testid={`clase-fila-${d.code}`}>
                <td><span className="inline-block h-2.5 w-2.5 rounded-sm mr-2 align-middle" style={{ background: colors[d.code] }} /><b>{d.code}</b> <span className="text-xs text-slate-500">{d.label}</span></td>
                <td className="num">{formatMoney(d.stock)}</td>
                <td className="num">{pct(d.pStock)}</td>
                <td className="num">{formatMoney(d.ventas)}</td>
                <td className={"num font-semibold " + (d.pVenta >= d.pStock ? "text-emerald-700" : "text-red-600")}>{pct(d.pVenta)}</td>
                <td className="num font-semibold">{rot(d.rotacion)}</td>
              </tr>
            ))}
            <tr className="font-bold border-t-2 border-slate-300">
              <td>Total</td><td className="num">{formatMoney(totS)}</td><td className="num">100 %</td>
              <td className="num">{formatMoney(totV)}</td><td className="num">100 %</td><td className="num">{rot(rotGlobal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Verde: la clase vende en proporción más de lo que pesa en stock; rojo: pesa más en stock de lo que vende.</p>
    </div>
  );
};
